import {
  createFileRoute,
  useParams,
  useNavigate,
} from '@tanstack/react-router';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

import { ProtectedRoute } from '@/components/protected-route';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

import JobMaterialsList from '@/components/materials/job-materials-list';

import { selectJobOverviewData, selectMaterialsByJobId } from '@/store/selectors';
import { addJobState, updateJobState, refreshMaterials } from '@/store/actions';
import { updateHelpModal, updateNotesModal } from '@/store/slices/appStateSlice';
import type { RootState } from '@/store';

export const Route = createFileRoute('/job/$jobId/$date/materials')({
  component: () => (
    <ProtectedRoute>
      <JobMaterialsPage />
    </ProtectedRoute>
  ),
});


function JobMaterialsPage() {
  const { jobId, date } = useParams({ from: '/job/$jobId/$date/materials' });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Use Redux selector to get all job-related data
  const { job, jobState } = useSelector((state: RootState) =>
    selectJobOverviewData(state, jobId)
  );

  // Get materials for this job
  const materials = useSelector((state: RootState) => selectMaterialsByJobId(state, jobId));

  // Get loading state for materials
  const materialsLoading = useSelector((state: RootState) => state.materials.loading);

  const handleRefreshMaterials = () => {
    if (job?.id) {
      dispatch(refreshMaterials({ job_id: job.id.toString() }));
    }
  };

  const handleGoBack = () => {
    navigate({ to: '/job/$jobId/$date', params: { jobId, date } });
  };

  const handleUpdateJobState = () => {
    if (typeof jobState !== 'undefined') {
      dispatch(updateJobState({
        ...jobState,
        confirmed_materials: moment().format('YYYY-MM-DD HH:mm:ss')
      }));
    } else {
      dispatch(addJobState({
        job_id: job.id,
        confirmed_materials: moment().format('YYYY-MM-DD HH:mm:ss')
      }));
    }
    navigate({ to: '/job/$jobId/$date', params: { jobId, date } });
  };

  const showNotesModal = () => {
    dispatch(updateNotesModal({
      show: true,
      canAssignTask: true,
    }));
  };

  const showHelpModal = () => {
    dispatch(updateHelpModal(true));
  };

  const handleCallOffice = () => {
    window.open('tel:01376519743', '_self');
  };

  if (!job) {
    return (
      <div className="col-span-2 flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600">Job not found</h2>
          <p className="text-gray-500">Could not load job data</p>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="col-span-2 h-full overflow-y-auto overflow-x-hidden max-h-[calc(100vh-5rem)]">
      <div className="p-8 flex justify-center">
        <div className="max-w-4xl w-full space-y-8">

          <div className="space-y-4">
            <h1 className="text-3xl font-bold leading-tight">
              Do you need a Material Number?
            </h1>
            <p className="text-base leading-relaxed my-2">
              In order to purchase any materials for this job you MUST be assigned a material number outlining what you can purchase. If you need one please call the office. Any Material Numbers that have been assigned will appear below.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold">Call office:</h2>
            <button
              onClick={handleCallOffice}
              className="text-2xl font-black text-blue-600 hover:text-blue-800 transition-colors"
            >
              01376 519743
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Material Numbers</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefreshMaterials}
                disabled={materialsLoading}
                className="flex items-center gap-2"
              >
                <svg
                  className={`w-4 h-4 ${materialsLoading ? 'animate-spin' : ''}`}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                >
                  <path d="M105.1 202.6c7.7-21.8 20.2-42.3 37.8-59.8c62.5-62.5 163.8-62.5 226.3 0L386.3 160 352 160c-17.7 0-32 14.3-32 32s14.3 32 32 32l111.5 0c0 0 0 0 0 0l.4 0c17.7 0 32-14.3 32-32l0-112c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 35.2L414.4 97.6c-87.5-87.5-229.3-87.5-316.8 0C73.2 122 55.6 150.7 44.8 181.4c-5.9 16.7 2.9 34.9 19.5 40.8s34.9-2.9 40.8-19.5zM39 289.3c-5 1.5-9.8 4.2-13.7 8.2c-4 4-6.7 8.8-8.1 14c-.3 1.2-.6 2.5-.8 3.8c-.3 1.7-.4 3.4-.4 5.1L16 432c0 17.7 14.3 32 32 32s32-14.3 32-32l0-35.1 17.6 17.5c0 0 0 0 0 0c87.5 87.4 229.3 87.4 316.7 0c24.4-24.4 42.1-53.1 52.9-83.8c5.9-16.7-2.9-34.9-19.5-40.8s-34.9 2.9-40.8 19.5c-7.7 21.8-20.2 42.3-37.8 59.8c-62.5 62.5-163.8 62.5-226.3 0l-.1-.1L125.6 352l34.4 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L48.4 288c-1.6 0-3.2 .1-4.8 .3s-3.1 .5-4.6 1z"/>
                </svg>
                {materialsLoading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>

            <JobMaterialsList materials={materials} />
          </div>

          <div className="flex gap-4 py-8 border-t border-gray-200">
            <Button
              onClick={jobState && jobState.confirmed_materials && jobState.confirmed_materials !== null ? handleGoBack : handleUpdateJobState}
              className="flex-1"
            >
              {jobState && jobState.confirmed_materials && jobState.confirmed_materials !== null ? "Back to Overview" : "Proceed with Job"}
            </Button>

            <Button
              variant="secondary"
              onClick={showHelpModal}
              className="flex-1"
            >
              I Need Assistance
            </Button>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}
